import { useTimeSettings } from '@mnd-frontend/contexts';
import { shortDateString } from '@mnd-frontend/dates';
import type { Sentiment } from '@mnd-frontend/graphql-schema';
import {
  Card,
  GREY_100,
  GREY_800,
  Icons,
  NEUTRAL_WHITE,
  PURPLE_900,
  SPACING_3,
  SocialAvatars,
  Text,
  Tooltip,
  TooltipContent,
  withLineClamp,
} from '@mnd-frontend/ui';
import { isValidSocialMediaUrl, localizeValue } from '@mnd-frontend/utils';
import type React from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { sentimentIcon } from './SentimentDropdown';

import type { ReportQuotation2Fragment } from '../../../../generated/types';
import { useReportTranslation } from '../../hooks';
import { AudioPlayer } from './AudioPlayer';

const getSocialMediaName = (url: string): string | null => {
  try {
    const { hostname } = new URL(url);

    return hostname.replace(/^www\./, '').replace('.com', '');
  } catch (e) {
    return null;
  }
};

const StyledImg = styled.img<{ $showEntireImage: boolean }>`
  grid-row: 1 / 1;
  grid-column: 1 / 1;

  aspect-ratio: 16/9;
  ${props =>
    props.$showEntireImage
      ? css`
          object-fit: contain;
          padding: ${SPACING_3};
        `
      : css`
          object-fit: cover;
        `};
  width: 100%;
  height: 100%;
  display: block;
`;

const DefaultSocialMediaImage = ({
  url,
  defaultColor = false,
}: {
  url: string;
  defaultColor?: boolean;
}) => {
  switch (getSocialMediaName(url)) {
    case 'x':
    case 'twitter':
      return <SocialAvatars.Twitter $square useDefaultColor={defaultColor} />;
    case 'facebook':
      return <SocialAvatars.Facebook $square useDefaultColor={defaultColor} />;
    case 'instagram':
      return <SocialAvatars.Instagram $square useDefaultColor={defaultColor} />;
    default:
      return null;
  }
};

const QuotationStyles = styled(Card)`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  background: ${GREY_100};
  box-shadow: none;
  h3 {
    ${withLineClamp(2)};
  }
  abbr {
    text-decoration: none;
  }
`;

const FeaturedStats = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  font-size: 0.75rem;
  color: ${GREY_800};
  align-items: center;
  flex-wrap: wrap;
`;

const FeaturedStat = styled.div<{ $withTooltip?: boolean }>`
  display: flex;
  align-items: center;

  ${props =>
    props.$withTooltip &&
    css`
      overflow: hidden;
      ${TooltipContent} {
        width: 100%;
        > div,
        > span {
          display: flex;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          align-items: center;
          margin: 0;
        }
      }
    `}
  span {
    color: ${GREY_800};
    margin: 0;
    gap: 0.25rem;
    font-size: 0.875rem;
  }
`;

const StatHeader = styled.div<{ $iconColor?: string }>`
  font-size: 0.625rem;
  display: flex;
  gap: 0.375rem;
  align-items: center;

  i,
  svg {
    flex-shrink: 0;
    width: 1.5rem;
    height: 1.5rem;
    color: ${({ $iconColor }) => $iconColor && $iconColor};
  }
`;

const SentimentLabel = styled.span`
  &::first-letter {
    text-transform: uppercase;
  }
  text-transform: lowercase;
`;

const QuotationBody = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
`;

const BodySection = styled.div`
  padding: 1.5rem;
`;

const BodyHeaderWrapper = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  > *:first-child {
    color: ${({ $color }) => $color};
  }
`;

const QuotationImage = ({
  src,
  onError,
  ...props
}: React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  onError: () => void;
}) => {
  const [showEntireImage, setShowEntireImage] = useState(false);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  useEffect(() => {
    const image = new Image();
    image.onload = e => {
      const img = e.target as HTMLImageElement;
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      setShowEntireImage(aspectRatio > 3 || aspectRatio < 1);
    };
    image.onerror = () => onErrorRef.current?.();
    image.src = src;
  }, [src]);

  return <StyledImg {...props} key={src} src={src} $showEntireImage={showEntireImage} />;
};

const IconWrapper = styled.div`
  position: relative;
  height: 4rem;
  width: 4rem;
  i,
  div,
  img {
    z-index: 1;
    border-radius: 0.75rem;
    width: 100%;
    height: 100%;
  }
  img {
    position: relative;
    z-index: 2;
    object-fit: contain;
  }
`;

const BackgroundPlate = styled.div`
  position: absolute;
  z-index: 1;
  background: ${NEUTRAL_WHITE};
  && {
    border-radius: 100%;
  }
`;

const FallbackLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  color: ${NEUTRAL_WHITE};
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: -0.125rem;
  white-space: nowrap;
  background: ${PURPLE_900};
  overflow: hidden;
`;

const Wrapper = styled.div`
  position: relative;
`;

const Overlay = styled.div`
  grid-row: 1 / 1;
  grid-column: 1 / 1;
  padding: 1.5rem 1.5rem 0;
  gap: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1;
`;

const TopContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr;
  position: relative;
  ${StyledImg} + ${Overlay} {
    padding-bottom: 1.5rem;
  }
`;

const CardLinkWrapper = styled.a`
  text-decoration: none;
  color: unset;
  &:hover {
    text-decoration: underline;
    color: unset;
  }
`;

const AudioWrapper = styled.div`
  margin-top: 1rem;
`;

export const Quotation = memo(
  ({
    quotation,
    showSentiment,
    showPrValue,
    showReach,
    showEngagement,
    sentiment,
    brandColor,
  }: {
    quotation: ReportQuotation2Fragment;
    showSentiment?: boolean;
    showPrValue: boolean;
    showReach: boolean;
    showEngagement: boolean;
    sentiment?: Sentiment;
    isPrint?: boolean;
    brandColor?: string;
  }) => {
    const { t, lng } = useReportTranslation();
    const {
      material: { timezone },
    } = useTimeSettings();
    const [imageError, setImageError] = useState<string | null>(null);
    const time = quotation.publishedAt
      ? shortDateString(quotation.publishedAt, timezone, lng)
      : null;
    const socialMediaQuotation = isValidSocialMediaUrl(quotation.url);

    return (
      <Wrapper>
        <QuotationStyles>
          <TopContainer>
            {quotation.imageUrl && imageError !== quotation.imageUrl && (
              <QuotationImage
                src={quotation.imageUrl}
                alt=""
                onError={() => setImageError(quotation.imageUrl)}
              />
            )}
            <Overlay>
              <IconWrapper>
                {socialMediaQuotation ? (
                  <DefaultSocialMediaImage url={quotation.url} defaultColor />
                ) : quotation.iconUrlNoFallback ? (
                  <>
                    <BackgroundPlate />
                    <img src={quotation.iconUrlNoFallback} alt="icon" />
                  </>
                ) : (
                  <FallbackLogoWrapper>
                    <p>{quotation.source}</p>
                  </FallbackLogoWrapper>
                )}
              </IconWrapper>
            </Overlay>
          </TopContainer>
          <QuotationBody>
            <BodySection>
              <BodyHeaderWrapper $color={brandColor}>
                <Text as="span" variant="labelSmall">
                  {quotation.source}
                </Text>
                {quotation.publishedAt && (
                  <Text as="span" variant="labelSmall">
                    <time dateTime={quotation.publishedAt}>{time}</time>
                  </Text>
                )}
              </BodyHeaderWrapper>
              <CardLinkWrapper href={quotation.url} target="_blank" rel="noopener">
                <Text as="h3" variant="subheading">
                  {quotation.title}
                </Text>
              </CardLinkWrapper>
              {quotation.audioUrl && !!quotation.audioHitlines?.length && (
                <AudioWrapper>
                  <AudioPlayer audioUrl={quotation.audioUrl} hitlines={quotation.audioHitlines} />
                </AudioWrapper>
              )}
            </BodySection>
            <BodySection>
              <FeaturedStats>
                {showReach && (
                  <FeaturedStat $withTooltip>
                    <Tooltip content={t('reports_reach-description')}>
                      <StatHeader $iconColor={brandColor}>
                        <Icons.Reach />
                        <span>
                          {quotation.reach ? (
                            <abbr title={quotation.reach.toString()}>
                              {localizeValue(quotation.reach, { abbrev: true })}
                            </abbr>
                          ) : (
                            'N/A'
                          )}
                        </span>
                      </StatHeader>
                    </Tooltip>
                  </FeaturedStat>
                )}
                {showEngagement && isValidSocialMediaUrl(quotation.url) && (
                  <FeaturedStat $withTooltip>
                    <Tooltip content={t('reports_engagement-description')}>
                      <StatHeader $iconColor={brandColor}>
                        <Icons.Share />

                        <span>
                          {quotation.engagement ? (
                            <abbr title={quotation.engagement.toString()}>
                              {localizeValue(quotation.engagement, { abbrev: true })}
                            </abbr>
                          ) : (
                            'N/A'
                          )}
                        </span>
                      </StatHeader>
                    </Tooltip>
                  </FeaturedStat>
                )}
                {showPrValue && (
                  <FeaturedStat $withTooltip>
                    <Tooltip content={t('reports_ad-value-description')}>
                      <StatHeader $iconColor={brandColor}>
                        <Icons.Diamond />
                        <span>
                          {quotation.prValueCurrency && quotation.prValue ? (
                            <abbr title={quotation.prValue.toString()}>
                              {localizeValue(quotation.prValue, { abbrev: true })}
                            </abbr>
                          ) : (
                            'N/A'
                          )}
                        </span>
                      </StatHeader>
                    </Tooltip>
                  </FeaturedStat>
                )}
                {showSentiment && sentiment && (
                  <FeaturedStat>
                    <Tooltip content={t('reports_sentiment-description')}>
                      <StatHeader $iconColor={brandColor}>
                        {sentimentIcon(sentiment)}
                        <SentimentLabel>{sentiment}</SentimentLabel>
                      </StatHeader>
                    </Tooltip>
                  </FeaturedStat>
                )}
              </FeaturedStats>
            </BodySection>
          </QuotationBody>
        </QuotationStyles>
      </Wrapper>
    );
  },
);
